import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, RotateCcw, Terminal, Save } from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

/**
 * TerminalGame - Hitchhiker's Guide to the Galaxy
 * ================================================
 * 
 * A retro CRT-style terminal interface for playing the classic
 * Infocom text adventure game via Z-Machine backend.
 * 
 * Features:
 * - Real Z-Machine game via ifvms backend
 * - Persistent saves to database (one per user)
 * - Session recovery on page reload
 * - Auto-scroll and typewriter effects
 * - CRT scanline visual effects
 */

const STORAGE_KEY = 'hhg_game_session';
const API_BASE = '/api/game';

// Command shortcuts - convert common phrases to Z-Machine short commands
const COMMAND_SHORTCUTS = {
    // Directions
    'go north': 'n', 'go n': 'n', 'north': 'n', 'pohjoinen': 'n',
    'go south': 's', 'go s': 's', 'south': 's', 'etelä': 'n',
    'go east': 'e', 'go e': 'e', 'east': 'e', 'itä': 'e',
    'go west': 'w', 'go w': 'w', 'west': 'w', 'länsi': 'w',
    'go up': 'u', 'up': 'u', 'ylös': 'u',
    'go down': 'd', 'down': 'd', 'alas': 'd',
    'go in': 'in', 'go inside': 'in', 'enter': 'in', 'sisään': 'in',
    'go out': 'out', 'go outside': 'out', 'leave': 'out', 'ulos': 'out',
    // Common commands
    'look around': 'l', 'look': 'l', 'katso': 'l',
    'inventory': 'i', 'inv': 'i', 'tavarat': 'i',
    'wait': 'z', 'odota': 'z',
    'again': 'g', 'uudelleen': 'g',
    // Hitchhiker's specific
    'get up': 'stand up', 'nouse': 'stand up',
    'turn on the light': 'turn on light',
    'switch on light': 'turn on light',
};

// Process command through shortcuts
const processCommand = (input) => {
    const lower = input.toLowerCase().trim();
    
    // Check exact matches first
    if (COMMAND_SHORTCUTS[lower]) {
        return COMMAND_SHORTCUTS[lower];
    }
    
    // Check for "examine X" → "x X" pattern
    if (lower.startsWith('examine ')) {
        return 'x ' + input.substring(8);
    }
    if (lower.startsWith('look at ')) {
        return 'x ' + input.substring(8);
    }
    if (lower.startsWith('katso ')) {
        return 'x ' + input.substring(6);
    }
    
    // Check for "take X" / "get X" / "pick up X"
    if (lower.startsWith('pick up ')) {
        return 'take ' + input.substring(8);
    }
    if (lower.startsWith('grab ')) {
        return 'take ' + input.substring(5);
    }
    if (lower.startsWith('ota ')) {
        return 'take ' + input.substring(4);
    }
    
    return input;
};

const TerminalGame = ({ user, onClose }) => {
    const { t } = useI18n(user?.club?.language);
    const [sessionId, setSessionId] = useState(null);
    const [history, setHistory] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasSavedGame, setHasSavedGame] = useState(false);
    const [showSaveConfirm, setShowSaveConfirm] = useState(false);
    const [turnCount, setTurnCount] = useState(0);
    const [sceneImage, setSceneImage] = useState(null);
    const [sceneType, setSceneType] = useState('dark');
    
    const contentRef = useRef(null);
    const inputRef = useRef(null);

    // Get user ID for saves
    const userId = user?.name || null;

    // Auto-scroll to bottom
    const scrollToBottom = useCallback(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = contentRef.current.scrollHeight;
        }
    }, []);

    // Add text to history
    const addToHistory = useCallback((text, type = 'output') => {
        setHistory(prev => [...prev, { text, type, timestamp: Date.now() }]);
    }, []);

    // Fetch scene image based on game output
    const fetchSceneImage = useCallback(async (gameOutput) => {
        try {
            const response = await fetch(`${API_BASE}/scene-image`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ gameOutput })
            });
            
            const data = await response.json();
            
            if (data.success && data.imageUrl) {
                setSceneImage(data.imageUrl);
                setSceneType(data.sceneType || 'general');
            }
        } catch (err) {
            console.error('Failed to fetch scene image:', err);
        }
    }, []);

    // Parse and clean game output - removes echoed command and prompts
    const cleanOutput = (text, command = '') => {
        if (!text) return '';
        
        let cleaned = text;
        
        // Remove the echoed command from the first line (Z-Machine echoes input)
        if (command) {
            const lines = cleaned.split('\n');
            // Check if first line is the echoed command (case-insensitive)
            if (lines.length > 0 && lines[0].toLowerCase().trim() === command.toLowerCase().trim()) {
                lines.shift(); // Remove first line
                cleaned = lines.join('\n');
            }
        }
        
        return cleaned
            .replace(/^>/gm, '') // Remove leading prompts
            .replace(/\n{3,}/g, '\n\n') // Reduce excessive newlines
            .trim();
    };

    // Check if user has a saved game
    const checkSavedGame = useCallback(async () => {
        if (!userId) return false;
        
        try {
            const response = await fetch(`${API_BASE}/has-save/${encodeURIComponent(userId)}`);
            const data = await response.json();
            setHasSavedGame(data.hasSave);
            return data.hasSave;
        } catch {
            return false;
        }
    }, [userId]);

    // Start new game session (fresh start)
    const startNewGame = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_BASE}/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setSessionId(data.sessionId);
                localStorage.setItem(STORAGE_KEY, data.sessionId);
                setHistory([]);
                const introText = cleanOutput(data.output);
                addToHistory(introText, 'intro');
                setIsInitialized(true);
                setHasSavedGame(false);
                setTurnCount(0);
                // Fetch initial scene image (dark room)
                fetchSceneImage(introText);
            } else {
                throw new Error(data.error || 'Failed to start game');
            }
        } catch (err) {
            console.error('Start game error:', err);
            setError(formatMemberText(t.terminalGame.startFailed, { message: err.message }));
        } finally {
            setIsLoading(false);
        }
    }, [userId, addToHistory]);

    // Restore saved game (replays commands on server)
    const restoreSavedGame = useCallback(async () => {
        if (!userId) {
            await startNewGame();
            return;
        }
        
        setIsLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_BASE}/restore`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId })
            });
            
            const data = await response.json();
            
            if (data.success && data.hasSave) {
                setSessionId(data.sessionId);
                localStorage.setItem(STORAGE_KEY, data.sessionId);
                
                // Restore output history if available (for display)
                if (data.outputHistory && Array.isArray(data.outputHistory)) {
                    setHistory(data.outputHistory);
                } else {
                    setHistory([]);
                }
                
                // Show restore confirmation with current game state
                addToHistory(data.output, 'system');
                setIsInitialized(true);
                setTurnCount(data.turnCount || 0);
                
                // Fetch scene image for current location
                if (data.output) {
                    fetchSceneImage(data.output);
                }
            } else {
                // No save found, start fresh
                await startNewGame();
            }
        } catch (err) {
            console.error('Restore game error:', err);
            // Fall back to new game
            await startNewGame();
        } finally {
            setIsLoading(false);
        }
    }, [userId, startNewGame, addToHistory, fetchSceneImage]);

    // Check existing memory session
    const checkSession = useCallback(async (storedSessionId) => {
        try {
            const response = await fetch(`${API_BASE}/check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId: storedSessionId })
            });
            
            const data = await response.json();
            return data.exists;
        } catch {
            return false;
        }
    }, []);

    // Send command to game
    const sendCommand = useCallback(async (command) => {
        if (!sessionId || isLoading) return;
        
        // Process command through shortcuts
        const processedCommand = processCommand(command);
        
        setIsLoading(true);
        addToHistory(`> ${command}`, 'command');
        
        try {
            const response = await fetch(`${API_BASE}/command`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId, command: processedCommand, userId })
            });
            
            const data = await response.json();
            
            if (data.success) {
                const cleanedOutput = cleanOutput(data.output, processedCommand);
                addToHistory(cleanedOutput, 'output');
                if (data.turnCount) {
                    setTurnCount(data.turnCount);
                }
                // Fetch scene image for movement commands or look
                const lowerCmd = processedCommand.toLowerCase();
                if (['n', 's', 'e', 'w', 'u', 'd', 'l', 'look', 'in', 'out'].includes(lowerCmd) || 
                    lowerCmd.startsWith('go ') || lowerCmd.includes('light')) {
                    fetchSceneImage(cleanedOutput);
                }
            } else if (data.code === 'SESSION_NOT_FOUND' || data.code === 'SESSION_EXPIRED') {
                setError(t.terminalGame.sessionExpired);
                setSessionId(null);
                localStorage.removeItem(STORAGE_KEY);
            } else {
                throw new Error(data.error || 'Command failed');
            }
        } catch (err) {
            console.error('Command error:', err);
            addToHistory(formatMemberText(t.terminalGame.errorPrefix, { message: err.message }), 'error');
        } finally {
            setIsLoading(false);
        }
    }, [sessionId, isLoading, userId, addToHistory]);

    // Save game manually
    const saveGame = useCallback(async () => {
        if (!sessionId || !userId) return;
        
        try {
            const response = await fetch(`${API_BASE}/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    sessionId, 
                    userId,
                    outputHistory: history
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setShowSaveConfirm(true);
                setTimeout(() => setShowSaveConfirm(false), 2000);
            }
        } catch (err) {
            console.error('Save error:', err);
        }
    }, [sessionId, userId, history]);

    // Close game session (with save)
    const closeGame = useCallback(async () => {
        if (sessionId && userId) {
            try {
                await fetch(`${API_BASE}/close`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        sessionId, 
                        userId,
                        saveBeforeClose: true,
                        outputHistory: history
                    })
                });
            } catch {
                // Ignore close errors
            }
        } else if (sessionId) {
            // Not logged in, just close
            try {
                await fetch(`${API_BASE}/close`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId })
                });
            } catch {
                // Ignore
            }
        }
        localStorage.removeItem(STORAGE_KEY);
        onClose();
    }, [sessionId, userId, history, onClose]);

    // Restart game (delete save and start fresh)
    const handleRestart = async () => {
        // Close current session
        if (sessionId) {
            try {
                await fetch(`${API_BASE}/close`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId })
                });
            } catch {
                // Ignore
            }
        }
        
        // Delete save if exists
        if (userId) {
            try {
                await fetch(`${API_BASE}/save`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId })
                });
            } catch {
                // Ignore
            }
        }
        
        setSessionId(null);
        setHistory([]);
        setError(null);
        setTurnCount(0);
        localStorage.removeItem(STORAGE_KEY);
        
        await startNewGame();
    };

    // Initialize on mount
    useEffect(() => {
        const init = async () => {
            const storedSessionId = localStorage.getItem(STORAGE_KEY);
            
            // First check if there's an active session in memory
            if (storedSessionId) {
                const exists = await checkSession(storedSessionId);
                
                if (exists) {
                    setSessionId(storedSessionId);
                    setIsInitialized(true);
                    addToHistory(t.terminalGame.sessionRestored, 'system');
                    return;
                }
            }
            
            // No active session, check for database save
            const hasSave = await checkSavedGame();
            
            if (hasSave) {
                await restoreSavedGame();
            } else {
                await startNewGame();
            }
        };
        
        init();
    }, [checkSession, checkSavedGame, restoreSavedGame, startNewGame, addToHistory]);

    // Auto-scroll when history updates
    useEffect(() => {
        scrollToBottom();
    }, [history, scrollToBottom]);

    // Focus input when ready
    useEffect(() => {
        if (isInitialized && !isLoading && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isInitialized, isLoading]);

    // Handle input submission
    const handleSubmit = (e) => {
        e.preventDefault();
        const command = inputValue.trim();
        
        if (!command) return;
        
        // Handle exit command
        if (command.toLowerCase() === 'exit' || command.toLowerCase() === 'quit') {
            closeGame();
            return;
        }
        
        // Handle save command - manual save of history
        if (command.toLowerCase() === 'save') {
            if (userId) {
                saveGame();
                addToHistory('> save', 'command');
                addToHistory(t.terminalGame.historySaved, 'system');
            } else {
                addToHistory('> save', 'command');
                addToHistory(t.terminalGame.loginToSave, 'system');
            }
            setInputValue('');
            return;
        }
        
        sendCommand(command);
        setInputValue('');
    };

    // Handle keyboard shortcuts
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            closeGame();
        }
    };

    return (
        <div className="terminal-game-overlay" onKeyDown={handleKeyDown}>
            {/* Scene Background Image */}
            {sceneImage && (
                <div 
                    className="terminal-scene-bg"
                    style={{ backgroundImage: `url(${sceneImage})` }}
                />
            )}
            
            {/* CRT Scanlines Effect */}
            <div className="crt-scanlines" />
            
            <div className={`terminal-game-container ${sceneImage ? 'with-scene' : ''}`}>
                {/* Header */}
                <div className="terminal-game-header">
                    <div className="terminal-game-title">
                        <Terminal size={18} />
                        <span>THE HITCHHIKER'S GUIDE TO THE GALAXY</span>
                    </div>
                    <div className="terminal-game-controls">
                        {/* Save indicator */}
                        {showSaveConfirm && (
                            <span className="terminal-save-confirm">{t.terminalGame.saved}</span>
                        )}
                        
                        {/* Manual save button (only for logged in users) */}
                        {userId && (
                            <button 
                                onClick={saveGame} 
                                className="terminal-btn" 
                                title={t.terminalGame.saveGame}
                                disabled={isLoading || !sessionId}
                            >
                                <Save size={16} />
                            </button>
                        )}
                        
                        <button 
                            onClick={handleRestart} 
                            className="terminal-btn" 
                            title={t.terminalGame.restartTitle}
                            disabled={isLoading}
                        >
                            <RotateCcw size={16} />
                        </button>
                        <button 
                            onClick={closeGame} 
                            className="terminal-btn terminal-close-btn" 
                            title={t.terminalGame.closeAndSave}
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="terminal-game-content" ref={contentRef}>
                    {/* Error Message */}
                    {error && (
                        <div className="terminal-error">
                            <p>{error}</p>
                            <button onClick={startNewGame} className="terminal-restart-btn">
                                {t.terminalGame.startNew}
                            </button>
                        </div>
                    )}

                    {/* History */}
                    {history.map((entry, idx) => (
                        <div 
                            key={idx} 
                            className={`terminal-entry terminal-${entry.type}`}
                        >
                            {entry.text.split('\n').map((line, lineIdx) => (
                                <div key={lineIdx} className="terminal-line">
                                    {line || '\u00A0'}
                                </div>
                            ))}
                        </div>
                    ))}

                    {/* Loading Indicator */}
                    {isLoading && (
                        <div className="terminal-loading">
                            <span className="terminal-cursor blink">█</span>
                            <span> Thinking...</span>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                <form onSubmit={handleSubmit} className="terminal-input-area">
                    <span className="terminal-prompt-char">&gt;</span>
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="terminal-input"
                        placeholder={isLoading ? t.terminalGame.wait : t.terminalGame.commandPlaceholder}
                        disabled={isLoading || !isInitialized}
                        autoComplete="off"
                        spellCheck="false"
                    />
                </form>

                {/* Footer */}
                <div className="terminal-game-footer">
                    <span>DON'T PANIC</span>
                    <span className="terminal-divider">•</span>
                    <span>N/S/E/W, LOOK, TAKE, I, X [tutki]</span>
                    {turnCount > 0 && (
                        <>
                            <span className="terminal-divider">•</span>
                            <span>{formatMemberText(t.terminalGame.turn, { count: turnCount })}</span>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TerminalGame;
